/**
 * TransitionMatrixView — Brief 4 · Gestor · Matriz de Transição (ADR-031 Eixo O).
 *
 * Grid N×N dos playbooks ATIVOS: linha = origem (`from`), coluna = destino (`to`).
 * Cada célula mostra a policy vigente lida de `from.transitionPolicy.pairs` (pair
 * com `toId === to.id`); diagonal (from==to) = N/A; par sem entrada = "padrão Lume"
 * (honest-defer — ADR-031 define defaults por vertical, não um valor universal por
 * par, então NÃO inventamos policy). Clicar numa célula abre o editor inline de 3
 * policies (gate `playbook-author`) e salva via PATCH no playbook `from` (substitui
 * `transitionPolicy` inteiro, If-Match na version do `from`).
 *
 * Honest-defer (sem fonte no wire de Playbooks): detecção de override / justificativa
 * por par, lock por Bond adversarial-strict (Regulated), "Resetar para defaults",
 * "Exportar matriz", pré-visualização de impacto e a visão "lista de pares". Esses
 * itens do gabarito de design são scaffolding/feature sem endpoint — não fabricados.
 */

import { useMemo, useState } from 'react';

import { ApiError } from '../../lib/api-client';
import { useToastStore } from '../../stores/toast-store';

import { POLICY_META, TRANSITION_POLICIES, isTransitionPolicy } from './types';
import { useUpdateTransitionPolicy } from './use-transition-matrix';

import type { TransitionPolicy } from './types';
import type { PlaybookDto } from '../playbooks/types';

export interface TransitionMatrixViewProps {
  playbooks: PlaybookDto[];
  canManage: boolean;
}

interface CellSelection {
  from: PlaybookDto;
  to: PlaybookDto;
  current: TransitionPolicy | null;
}

/** Rótulo curto p/ cabeçalho/linha (o wire não tem `short`); nome completo no title. */
function shortLabel(name: string): string {
  const tail = name.includes('—') ? name.slice(name.lastIndexOf('—') + 1).trim() : name;
  return tail.length > 22 ? `${tail.slice(0, 21)}…` : tail;
}

/** Policy vigente da célula (from→to), lida dos pairs do `from`. null = padrão Lume. */
function policyFor(from: PlaybookDto, toId: string): TransitionPolicy | null {
  const pair = from.transitionPolicy.pairs.find((p) => p.toId === toId);
  if (pair === undefined) return null;
  return isTransitionPolicy(pair.policy) ? pair.policy : null;
}

export function TransitionMatrixView({
  playbooks,
  canManage,
}: TransitionMatrixViewProps): JSX.Element {
  const [selection, setSelection] = useState<CellSelection | null>(null);
  const update = useUpdateTransitionPolicy();
  const toast = useToastStore((s) => s.push);

  const pairCount = playbooks.length * Math.max(playbooks.length - 1, 0);
  // Conta APENAS pares que viram célula na matriz: toId de um playbook ATIVO e
  // != from. Pares com toId fora dos ativos (ex.: wildcards `*-suporte` ou ids
  // legados de seed) não aparecem como célula — incluí-los inflaria o subhead em
  // relação ao que o Gestor de fato vê. Honest: o número casa com as células.
  const configuredCount = useMemo(() => {
    const activeIds = new Set(playbooks.map((p) => p.id));
    return playbooks.reduce((sum, from) => {
      const seen = new Set<string>();
      for (const pair of from.transitionPolicy.pairs) {
        if (
          isTransitionPolicy(pair.policy) &&
          pair.toId !== from.id &&
          activeIds.has(pair.toId) &&
          !seen.has(pair.toId)
        ) {
          seen.add(pair.toId);
        }
      }
      return sum + seen.size;
    }, 0);
  }, [playbooks]);

  if (playbooks.length < 2) {
    return (
      <div className="transition-matrix-screen">
        <PageHead configuredCount={configuredCount} pairCount={pairCount} />
        <div className="tm-page">
          <div className="tm-empty" role="note">
            <h2 className="tm-empty__title">Configure playbooks primeiro</h2>
            <p className="tm-empty__sub">
              A matriz de transição precisa de pelo menos 2 playbooks ativos para mostrar pares de
              origem e destino.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleSave = (sel: CellSelection, policy: TransitionPolicy | null): void => {
    if (!canManage || update.isPending) return;
    const others = sel.from.transitionPolicy.pairs.filter((p) => p.toId !== sel.to.id);
    // policy null = "padrão Lume": remove o par (o backend substitui o objeto inteiro).
    const pairs =
      policy !== null ? [...others, { fromId: sel.from.id, toId: sel.to.id, policy }] : others;
    update.mutate(
      {
        fromId: sel.from.id,
        expectedVersion: sel.from.version,
        transitionPolicy: { pairs },
      },
      {
        onSuccess: () => {
          setSelection(null);
          toast({
            variant: 'success',
            title:
              policy !== null
                ? `Política ${POLICY_META[policy].label.toLowerCase()} salva`
                : 'Política removida — o par volta ao padrão Lume',
            description: `${sel.from.name} → ${sel.to.name}`,
            durationMs: 3000,
          });
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 409) {
            setSelection(null);
            toast({
              variant: 'info',
              title: 'A matriz foi atualizada por outra alteração',
              description: 'Recarregamos os playbooks. Abra a célula novamente.',
              durationMs: 5000,
            });
            return;
          }
          toast({
            variant: 'error',
            title: 'Não foi possível salvar a política.',
            durationMs: 6000,
          });
        },
      },
    );
  };

  return (
    <div className="transition-matrix-screen">
      <PageHead configuredCount={configuredCount} pairCount={pairCount} />

      <div className="tm-page">
        {!canManage && (
          <div className="tm-banner tm-banner--muted" role="note">
            <span>
              Você tem acesso de <strong>leitura</strong>. A edição das políticas de transição é
              restrita a quem pode editar playbooks — fale com o administrador do tenant.
            </span>
          </div>
        )}

        <div className="tm-grid-wrap">
          <div
            className="tm-grid"
            role="table"
            aria-label="Matriz de transição entre playbooks"
            style={{ gridTemplateColumns: `minmax(120px, auto) repeat(${playbooks.length}, 1fr)` }}
          >
            <div className="tm-grid__corner">
              <span>de ↓ / para →</span>
            </div>
            {playbooks.map((to) => (
              <div key={`h-${to.id}`} className="tm-grid__colhead" title={to.name}>
                {shortLabel(to.name)}
              </div>
            ))}

            {playbooks.map((from) => (
              <Row
                key={`r-${from.id}`}
                from={from}
                playbooks={playbooks}
                canManage={canManage}
                onPick={setSelection}
              />
            ))}
          </div>
        </div>

        <Legend />

        <div className="tm-banner tm-banner--muted" role="note">
          <span>
            A matriz lê e edita a política real de cada par. Em preparação: destacar o que difere do
            padrão Lume, bloqueio obrigatório em vínculos de conflito (planos regulados), prévia de
            impacto, «Voltar ao padrão» e «Exportar matriz».
          </span>
        </div>
      </div>

      {selection !== null && (
        <PolicyEditor
          selection={selection}
          canManage={canManage}
          isSaving={update.isPending}
          onClose={() => setSelection(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function PageHead({
  configuredCount,
  pairCount,
}: {
  configuredCount: number;
  pairCount: number;
}): JSX.Element {
  return (
    <header className="tm-pagehead">
      <div>
        <div className="tm-pagehead__crumb">Configuração</div>
        <h1 className="tm-pagehead__title">Matriz de Transição entre Playbooks</h1>
        <p className="tm-pagehead__sub">
          3 políticas canônicas ·{' '}
          <strong>
            {configuredCount}/{pairCount}
          </strong>{' '}
          pares com política definida
        </p>
      </div>
    </header>
  );
}

interface RowProps {
  from: PlaybookDto;
  playbooks: PlaybookDto[];
  canManage: boolean;
  onPick: (sel: CellSelection) => void;
}

function Row({ from, playbooks, canManage, onPick }: RowProps): JSX.Element {
  return (
    <div className="tm-grid__row" role="row">
      <div className="tm-grid__rowhead" title={from.name}>
        {shortLabel(from.name)}
      </div>
      {playbooks.map((to) => {
        if (from.id === to.id) {
          return (
            <div key={to.id} className="tm-cell tm-cell--diagonal" aria-hidden="true">
              <span className="tm-cell__na">—</span>
            </div>
          );
        }
        const policy = policyFor(from, to.id);
        const meta = policy !== null ? POLICY_META[policy] : null;
        const ariaLabel = `${from.name} para ${to.name}: ${
          meta !== null ? meta.label : 'padrão Lume'
        }`;
        const className = `tm-cell ${meta !== null ? meta.cls : 'tm-cell--default'}${
          canManage ? ' tm-cell--editable' : ''
        }`;
        const content = (
          <>
            <span className="tm-cell__icon" aria-hidden="true">
              {meta !== null ? meta.icon : '·'}
            </span>
            <span className="tm-cell__label">{meta !== null ? meta.short : 'padrão'}</span>
          </>
        );
        if (!canManage) {
          return (
            <div key={to.id} className={className} role="cell" aria-label={ariaLabel}>
              {content}
            </div>
          );
        }
        return (
          <button
            key={to.id}
            type="button"
            className={className}
            aria-label={`Editar política — ${ariaLabel}`}
            onClick={() => onPick({ from, to, current: policy })}
          >
            {content}
          </button>
        );
      })}
    </div>
  );
}

function Legend(): JSX.Element {
  return (
    <div className="tm-legend" aria-label="Legenda das políticas">
      {TRANSITION_POLICIES.map((p) => {
        const meta = POLICY_META[p];
        return (
          <div key={p} className="tm-legend__item">
            <span className={`tm-legend__swatch ${meta.cls}`} aria-hidden="true" />
            <span>
              {meta.icon} <strong>{meta.short}</strong> — {meta.label}: {meta.desc}
            </span>
          </div>
        );
      })}
      <div className="tm-legend__item">
        <span className="tm-legend__swatch tm-cell--default" aria-hidden="true" />
        <span>
          · <strong>padrão</strong> — sem política explícita; herda o comportamento padrão da Lume.
        </span>
      </div>
    </div>
  );
}

interface PolicyEditorProps {
  selection: CellSelection;
  canManage: boolean;
  isSaving: boolean;
  onClose: () => void;
  onSave: (sel: CellSelection, policy: TransitionPolicy | null) => void;
}

function PolicyEditor({
  selection,
  canManage,
  isSaving,
  onClose,
  onSave,
}: PolicyEditorProps): JSX.Element {
  // null = "padrão Lume" (sem política explícita — remove o par ao salvar).
  const [policy, setPolicy] = useState<TransitionPolicy | null>(selection.current);

  return (
    <aside
      className="tm-editor"
      aria-label={`Política de ${selection.from.name} para ${selection.to.name}`}
    >
      <div className="tm-editor__head">
        <h2 className="tm-editor__title">
          {selection.from.name} <span className="tm-editor__arrow">→</span> {selection.to.name}
        </h2>
        <button type="button" className="tm-link" onClick={onClose}>
          Fechar
        </button>
      </div>

      <p className="tm-editor__hint">
        {selection.current !== null
          ? `Política atual: ${POLICY_META[selection.current].label}.`
          : 'Sem política explícita — herda o comportamento padrão da Lume.'}
      </p>

      <div className="tm-radios" role="radiogroup" aria-label="Política da transição">
        {TRANSITION_POLICIES.map((p) => {
          const meta = POLICY_META[p];
          const selected = policy === p;
          return (
            <label key={p} className={`tm-radio${selected ? ' tm-radio--selected' : ''}`}>
              <input
                type="radio"
                name="tm-policy"
                checked={selected}
                onChange={() => setPolicy(p)}
                disabled={!canManage || isSaving}
              />
              <span className="tm-radio__main">
                <span className="tm-radio__label">
                  {meta.icon} {meta.label}
                </span>
                <span className="tm-radio__desc">{meta.desc}</span>
              </span>
            </label>
          );
        })}
        <label className={`tm-radio${policy === null ? ' tm-radio--selected' : ''}`}>
          <input
            type="radio"
            name="tm-policy"
            checked={policy === null}
            onChange={() => setPolicy(null)}
            disabled={!canManage || isSaving}
          />
          <span className="tm-radio__main">
            <span className="tm-radio__label">· Padrão Lume</span>
            <span className="tm-radio__desc">
              Sem política explícita — o par herda o comportamento padrão da Lume.
            </span>
          </span>
        </label>
      </div>

      <div className="tm-editor__foot">
        <button type="button" className="tm-btn tm-btn--ghost" onClick={onClose}>
          Cancelar
        </button>
        <button
          type="button"
          className="tm-btn tm-btn--primary"
          disabled={!canManage || isSaving || policy === selection.current}
          onClick={() => onSave(selection, policy)}
        >
          {isSaving ? 'Salvando…' : 'Salvar política'}
        </button>
      </div>
    </aside>
  );
}
